/**
 * 画面の分野と絵（#240・#242、docs/94 B-2・B-7）。左のメニューの項目・戻るボタンの絵・メニューの印が同じ表を使う。
 *   ・分野＝左のメニューの項目（ホーム・{project}・{schedule}・見積書・請求書・入金・{vendorQuote}・見積依頼・{order}・仕入・{customer}・送付状・設定）
 *   ・メニューに項目の無い画面（領収書・{paymentNotice}・送付の記録・テンプレートなど）は上の画面の分野＝その項目に印（areaOfPath）
 *   ・戻るボタンの文字（backLabelOf）：書類＝種類＋番号（画面が usePageName で渡す短い名前）／一覧＝「〜一覧」／{project}・{customer}の詳細＝種類だけ／ホーム
 */
import type { Labels } from "@genbaryoku/contract";
import type { ReactNode } from "react";

export type Area = "home" | "groups" | "schedule" | "estimates" | "deliveryNotes" | "invoices" | "payments" | "vendorQuotes" | "rfqs" | "orders" | "purchases" | "partners" | "coverLetters" | "settings";

/** 道の頭 → 分野（上から順に見る）。メニューに項目の無い画面は上の画面の分野 */
const PREFIX: ReadonlyArray<readonly [RegExp, Area]> = [
  [/^\/home(\/|$)/, "home"],
  [/^\/groups\/[^/]+\/schedule(\/|$)/, "schedule"],
  [/^\/groups(\/|$)/, "groups"],
  [/^\/schedule(\/|$)/, "schedule"],
  [/^\/schedules\//, "schedule"],
  [/^\/estimates(\/|$)/, "estimates"],
  [/^\/delivery-notes(\/|$)/, "deliveryNotes"],
  [/^\/acceptances(\/|$)/, "deliveryNotes"],
  [/^\/invoices(\/|$)/, "invoices"],
  [/^\/closing(\/|$)/, "invoices"],
  [/^\/recurring(\/|$)/, "invoices"],
  [/^\/receipts(\/|$)/, "invoices"],
  [/^\/payments(\/|$)/, "payments"],
  [/^\/vendor-quotes(\/|$)/, "vendorQuotes"],
  [/^\/rfqs(\/|$)/, "rfqs"],
  [/^\/orders(\/|$)/, "orders"],
  [/^\/purchases(\/|$)/, "purchases"],
  [/^\/payment-notices(\/|$)/, "purchases"],
  [/^\/partners(\/|$)/, "partners"],
  [/^\/cover-letters(\/|$)/, "coverLetters"],
  [/^\/deliveries(\/|$)/, "coverLetters"],
  [/^\/(settings|account)(\/|$)/, "settings"],
];
export function areaOfPath(path: string): Area | null {
  const p = path.split(/[?#]/)[0] ?? "";
  for (const [re, a] of PREFIX) if (re.test(p)) return a;
  return null;
}
/** 左のメニューの項目の道 → 分野（メニューの印を付けるかを決める） */
export const AREA_HOME: Record<Area, string> = {
  home: "/home",
  groups: "/groups",
  schedule: "/schedule",
  estimates: "/estimates",
  deliveryNotes: "/delivery-notes",
  invoices: "/invoices",
  payments: "/payments",
  vendorQuotes: "/vendor-quotes",
  rfqs: "/rfqs",
  orders: "/orders",
  purchases: "/purchases",
  partners: "/partners",
  coverLetters: "/cover-letters",
  settings: "/settings",
};

/** 一覧の画面（ここへ戻るボタンは「〜一覧」） */
const LIST_ROOTS = new Set(["/groups", "/schedule", "/estimates", "/delivery-notes", "/invoices", "/payments", "/vendor-quotes", "/rfqs", "/orders", "/purchases", "/payment-notices", "/partners", "/cover-letters"]);

/**
 * 戻るボタンの文字（行き先の名前を短く）。short＝その画面が登録した短い名前（書類の種類＋番号など）。
 * 「を」「の」を入れない（#238。一覧は「見積書一覧」）
 */
export function backLabelOf(path: string, name: string, short: string | null | undefined, L: Pick<Labels, "project" | "customer">): string {
  if (short) return short;
  const p = path.split(/[?#]/)[0] ?? "";
  if (LIST_ROOTS.has(p)) return name.endsWith("一覧") ? name : `${name}一覧`;
  if (/^\/groups\/[^/]+$/.test(p) && p !== "/groups/new") return L.project;
  if (/^\/partners\/[^/]+$/.test(p) && p !== "/partners/new") return L.customer;
  return name;
}

/** 書類の戻るボタンの短い名前：種類＋番号（番号が無ければ種類だけ）。「見積書 Q-0001」 */
export const docShort = (kind: string, no: string | null | undefined): string => (no ? `${kind} ${no}` : kind);

/** 分野の絵（24×24 の線。左のメニューと同じ形） */
export const AREA_ICON_PATH: Record<Area, ReactNode> = {
  home: <path d="M3 11l9-8 9 8v9a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />,
  groups: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,
  schedule: <path d="M4 4v16h16M8 8h6M10 12h7M8 16h4" />,
  estimates: <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM14 3v5h5M9 13h6M9 17h6" />,
  /** 納品書（#266）：トラック（品物を届ける） */
  deliveryNotes: (
    <>
      <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2M15 18H9M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.62l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
      <circle cx="17" cy="18" r="2" />
      <circle cx="7" cy="18" r="2" />
    </>
  ),
  invoices: <path d="M5 3h14v18l-2.5-1.5L14 21l-2-1.5L10 21l-2.5-1.5L5 21zM9 8h6M9 12h6M9 16h4" />,
  payments: (
    <>
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <circle cx="12" cy="12" r="3" />
      <path d="M6 12h.01M18 12h.01" />
    </>
  ),
  vendorQuotes: <path d="M9 3h6l5 5v13a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-6M15 3v5h5M3 12h9M9 9l3 3-3 3" />,
  /** 見積依頼（#242）：紙にペン（依頼を書いて送る） */
  rfqs: <path d="M7 3h7l5 5v5M14 3v5h5M6 4v16a1 1 0 0 0 1 1h5M9 12h5M9 16h3M15.5 21l5-5-2-2-5 5V21z" />,
  orders: <path d="M9 4h6v3H9zM9 5H6a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-3M9 14l2 2 4-4" />,
  purchases: (
    <>
      <path d="M21 8l-9-5-9 5v8l9 5 9-5z" />
      <path d="M3 8l9 5 9-5M12 13v8" />
    </>
  ),
  partners: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  coverLetters: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </>
  ),
};

export function AreaIcon({ a, className }: { a: Area; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      {AREA_ICON_PATH[a]}
    </svg>
  );
}
